import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import { GlassButton } from "../../themes/UiElements";

const AllVoiceFilesList = () => {
  const navigate = useNavigate();

  const [voiceFiles, setVoiceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Approve modal state
  const [approveModal, setApproveModal] = useState({
    open: false,
    file: null,
    loading: false,
  });

  // Reject (revoke to pending) modal state
  const [rejectModal, setRejectModal] = useState({
    open: false,
    file: null,
    loading: false,
  });

  // Deny (reject to rejected) modal state
  const [denyModal, setDenyModal] = useState({
    open: false,
    file: null,
    loading: false,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch voice files
  const fetchVoiceFiles = async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        search: debouncedSearch,
        status: statusFilter,
        sortBy: "createdAt",
        sortOrder: "desc",
      };
      const response = await axios.get(`${API_ENDPOINT}/root/voice-files`, {
        params,
        ...getHeader(),
      });

      setVoiceFiles(response.data.data);
      setTotalCount(response.data.pagination.totalCount);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch voice files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoiceFiles();
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this voice file?")) {
      return;
    }

    try {
      await axios.delete(
        `${API_ENDPOINT}/voice/voice-files/${id}`,
        getHeader()
      );
      fetchVoiceFiles();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete voice file");
    }
  };

  // Open approve confirmation modal
  const handleApproveClick = (file) => {
    setApproveModal({
      open: true,
      file,
      loading: false,
    });
  };

  // Close approve modal
  const handleApproveClose = () => {
    setApproveModal({
      open: false,
      file: null,
      loading: false,
    });
  };

  // Confirm and approve voice file
  const handleApproveConfirm = async () => {
    setApproveModal((prev) => ({ ...prev, loading: true }));

    try {
      await axios.patch(
        `${API_ENDPOINT}/root/voice-files/${approveModal.file._id}/approve`,
        {},
        getHeader()
      );

      handleApproveClose();
      fetchVoiceFiles();

      // Optional: Show success message
      setError(""); // Clear any existing errors
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve voice file");
      setApproveModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Open reject (revoke to pending) confirmation modal
  const handleRejectClick = (file) => {
    setRejectModal({
      open: true,
      file,
      loading: false,
    });
  };

  // Close reject modal
  const handleRejectClose = () => {
    setRejectModal({
      open: false,
      file: null,
      loading: false,
    });
  };

  // Confirm and reject (revoke to pending) voice file
  const handleRejectConfirm = async () => {
    setRejectModal((prev) => ({ ...prev, loading: true }));

    try {
      await axios.patch(
        `${API_ENDPOINT}/root/voice-files/${rejectModal.file._id}/reject`,
        {},
        getHeader()
      );

      handleRejectClose();
      fetchVoiceFiles();

      // Optional: Show success message
      setError(""); // Clear any existing errors
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject voice file");
      setRejectModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Open deny (reject to rejected) confirmation modal
  const handleDenyClick = (file) => {
    setDenyModal({
      open: true,
      file,
      loading: false,
    });
  };

  // Close deny modal
  const handleDenyClose = () => {
    setDenyModal({
      open: false,
      file: null,
      loading: false,
    });
  };

  // Confirm and deny (reject to rejected) voice file
  const handleDenyConfirm = async () => {
    setDenyModal((prev) => ({ ...prev, loading: true }));

    try {
      await axios.patch(
        `${API_ENDPOINT}/root/voice-files/${denyModal.file._id}/deny`,
        {},
        getHeader()
      );

      handleDenyClose();
      fetchVoiceFiles();

      // Optional: Show success message
      setError(""); // Clear any existing errors
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject voice file");
      setDenyModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "approved":
        return "success";
      case "uploading":
        return "warning";
      case "failed":
        return "error";
      case "pending":
        return "info";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Voice Files
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => navigate("/home/upload-files")}
          >
            Upload New File
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by filename, user ID, category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="uploading">Uploading</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Refresh">
            <IconButton onClick={fetchVoiceFiles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>File ID</strong>
                </TableCell>
                <TableCell>
                  <strong>File Name</strong>
                </TableCell>
                <TableCell>
                  <strong>User ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Category</strong>
                </TableCell>
                <TableCell>
                  <strong>Size</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Uploaded</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : voiceFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {debouncedSearch || statusFilter
                        ? "No voice files found matching your criteria"
                        : "No voice files uploaded yet"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                voiceFiles.map((file) => (
                  <TableRow key={file._id} hover>
                    <TableCell>{file?.voiceFileId ?? "NA"}</TableCell>
                    <TableCell>
                      <Tooltip title={file.originalFileName}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.fileName}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{file.userId}</TableCell>
                    <TableCell>
                      <Chip
                        label={file.promptCategory}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{file.fileSizeReadable}</TableCell>
                    <TableCell>
                      <Chip
                        label={file.status}
                        size="small"
                        color={getStatusColor(file.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(file.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {file.uploadedBy}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/home/${file._id}/voice-files`)
                          }
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {file.status === "approved" && (
                        <Tooltip title="Reject">
                          <GlassButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(file)}
                          >
                            Reject
                          </GlassButton>
                        </Tooltip>
                      )}
                      {file.status === "pending" && (
                        <>
                          <Tooltip title="Approve">
                            <GlassButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveClick(file)}
                            >
                              Approve
                            </GlassButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <GlassButton
                              size="small"
                              color="error"
                              onClick={() => handleDenyClick(file)}
                            >
                              Reject
                            </GlassButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(file._id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Approve Confirmation Modal */}
      <Dialog
        open={approveModal.open}
        onClose={approveModal.loading ? null : handleApproveClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Voice File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve this voice file?
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>File Name:</strong> {approveModal.file?.fileName}
              </Typography>
              <Typography variant="body2">
                <strong>User ID:</strong> {approveModal.file?.userId}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {approveModal.file?.promptCategory}
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleApproveClose}
            disabled={approveModal.loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveConfirm}
            disabled={approveModal.loading}
            variant="contained"
            color="success"
            startIcon={
              approveModal.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <ApproveIcon />
              )
            }
          >
            {approveModal.loading ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject (revoke to pending) Confirmation Modal */}
      <Dialog
        open={rejectModal.open}
        onClose={rejectModal.loading ? null : handleRejectClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Voice File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reject this voice file? This will set its
            status to pending.
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>File Name:</strong> {rejectModal.file?.fileName}
              </Typography>
              <Typography variant="body2">
                <strong>User ID:</strong> {rejectModal.file?.userId}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {rejectModal.file?.promptCategory}
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRejectClose}
            disabled={rejectModal.loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            disabled={rejectModal.loading}
            variant="contained"
            color="error"
            startIcon={
              rejectModal.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CancelIcon />
              )
            }
          >
            {rejectModal.loading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deny (reject to rejected) Confirmation Modal */}
      <Dialog
        open={denyModal.open}
        onClose={denyModal.loading ? null : handleDenyClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Voice File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reject this voice file? This will set its
            status to rejected.
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>File Name:</strong> {denyModal.file?.fileName}
              </Typography>
              <Typography variant="body2">
                <strong>User ID:</strong> {denyModal.file?.userId}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {denyModal.file?.promptCategory}
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDenyClose}
            disabled={denyModal.loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDenyConfirm}
            disabled={denyModal.loading}
            variant="contained"
            color="error"
            startIcon={
              denyModal.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CancelIcon />
              )
            }
          >
            {denyModal.loading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllVoiceFilesList;
